import { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { captureException } from '@sentry/react';
import { useClientUser } from '@/hooks';
import { CLIENT_BY_ID } from '@/graphql/queries/users';
import { DELETE_TAG, REPLACE_ALWAYS_APPLY_CLIENTS, UPSERT_TAGS } from './graphql';
import { buildAlwaysApplyRows, mergeUpdates, sortByName, splitTags, stripForUpsert } from './utils';

/**
 * State and mutations for the custom tags panel. The save model is unchanged from the panel
 * this replaced: adding and deleting write immediately, everything else stages into `updates`
 * and lands on an explicit Save.
 */
export const useCustomTags = () => {
    const { tags, user_id } = useClientUser();
    const [notification, setNotification] = useState({});
    const [updates, setUpdates] = useState({});
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);

    const { owned, org } = useMemo(() => splitTags(tags, user_id), [tags, user_id]);
    const sorted = useMemo(() => sortByName(owned), [owned]);
    const orgSorted = useMemo(() => sortByName(org), [org]);
    const updated = useMemo(() => mergeUpdates(sorted, updates), [sorted, updates]);

    const [upsertTags, { loading: upsertLoading }] = useMutation(UPSERT_TAGS, {
        refetchQueries: [CLIENT_BY_ID],
        awaitRefetchQueries: true,
        onError: (e) => {
            console.error(e);
            captureException(e);
            setNotification({ severity: 'error', message: 'Error updating tags' });
        },
        onCompleted: () => {
            setUpdates({});
            setNotification({ severity: 'success', message: 'Tags saved successfully and sorted.' });
        },
    });

    const [replaceAlwaysApply] = useMutation(REPLACE_ALWAYS_APPLY_CLIENTS, {
        refetchQueries: [CLIENT_BY_ID],
        awaitRefetchQueries: true,
        onError: (e) => {
            console.error(e);
            captureException(e);
            setNotification({ severity: 'error', message: 'Error saving always-apply' });
        },
    });

    const [deleteTag, { loading: deleteLoading }] = useMutation(DELETE_TAG, {
        onError: (e) => {
            console.error(e);
            captureException(e);
            setNotification({ severity: 'error', message: 'Error deleting tag. Please try again.' });
        },
        update: (cache, { data: { delete_tags_by_pk } }) => {
            cache.evict(cache.identify(delete_tags_by_pk));
        },
    });

    const loading = upsertLoading || deleteLoading;

    // Writes straight through, as before, so the new tag has a tag_id to edit against.
    const addTag = () =>
        upsertTags({
            variables: {
                tags: [
                    {
                        client_id: user_id,
                        // The leading space sorts a new tag to the top until it's renamed.
                        tag: ` New Tag ${owned.length}`,
                        route_tag: false,
                        accessorial_tag: false,
                    },
                ],
            },
        });

    // Stage an edit for the open tag; nothing hits the server until save().
    const stage = (tag) => setUpdates((prev) => ({ ...prev, [tag.tag_id]: tag }));

    const save = async () => {
        const dirtyIds = Object.keys(updates);
        try {
            const res = await upsertTags({ variables: { tags: stripForUpsert(updated) } });
            if (res?.data && !res?.errors?.length && dirtyIds.length) {
                await replaceAlwaysApply({
                    variables: { tag_ids: dirtyIds, rows: buildAlwaysApplyRows(dirtyIds, updated) },
                });
            }
        } catch (e) {
            // both mutations surface their own message via onError
        }
    };

    const confirmDelete = async () => {
        const target = deleting;
        setDeleting(null);
        setEditing(null);
        if (!target) return;
        const res = await deleteTag({ variables: { tag_id: target.tag_id } });
        // Drop any staged edit for the deleted tag, or "unsaved changes" and Save outlive it.
        if (res?.data?.delete_tags_by_pk) {
            setUpdates((prev) => {
                const next = { ...prev };
                delete next[target.tag_id];
                return next;
            });
        }
    };

    return {
        state: {
            tags: updated,
            orgTags: orgSorted,
            user_id,
            loading,
            dirty: Object.keys(updates).length > 0,
            dirtyIds: new Set(Object.keys(updates)),
            notification,
            editing: editing ? updated.find((t) => t.tag_id === editing) || null : null,
            deleting,
        },
        callbacks: {
            addTag,
            stage,
            save,
            openEditor: (tag) => setEditing(tag.tag_id),
            closeEditor: () => setEditing(null),
            askDelete: setDeleting,
            cancelDelete: () => setDeleting(null),
            confirmDelete,
            clearNotification: () => setNotification({}),
        },
    };
};
